import type { ChatEmailDraft } from '../types/chat-email.types';

const HEADER = /^([A-Za-z][A-Za-z-]*):[ \t]*(.*)$/;

const HEADER_NAMES = new Set(['bcc', 'cc', 'from', 'subject', 'to']);

export const ChatEmailUtil = {
  /** `.` does not match `\r`, so a CRLF line would never reach the `$` in HEADER. */
  toLines({ text }: { text: string }) {
    return text.split(/\r?\n/);
  },

  /** Does not split inside a quoted display name: `"Smith, John" <j@x.com>` is one address. */
  splitAddressList({ value }: { value: string }) {
    const addresses: string[] = [];
    let current = '';
    let inQuotes = false;

    for (const char of value) {
      if (char === '"') inQuotes = !inQuotes;

      if (!inQuotes && (char === ',' || char === ';')) {
        addresses.push(current);
        current = '';
      } else {
        current += char;
      }
    }

    return [...addresses, current];
  },

  /** Commas stay literal between addresses, the separator RFC 6068 expects. */
  encodeAddressList({ value }: { value: string }) {
    return ChatEmailUtil.splitAddressList({ value })
      .map(address => address.trim())
      .filter(Boolean)
      .map(address => encodeURIComponent(address))
      .join(',');
  },

  parseEmail({ text }: { text: string }): ChatEmailDraft {
    const lines = ChatEmailUtil.toLines({ text });
    const headers = new Map<string, string>();
    let index = 0;
    let openHeader: string | null = null;

    for (; index < lines.length; index++) {
      const line = lines[index] ?? '';

      if (line.trim() === '') {
        index++;
        break;
      }

      const match = HEADER.exec(line);
      const name = (match?.[1] ?? '').toLowerCase();

      if (match && HEADER_NAMES.has(name)) {
        headers.set(name, (match[2] ?? '').trim());
        openHeader = name;
      } else {
        if (openHeader === null) break;

        const open = headers.get(openHeader) ?? '';

        // Only while the header is empty, so a first body line is never taken as its value.
        if (!open) {
          headers.set(openHeader, line.trim());
        } else if (/[,;]$/.test(open) || /^\s/.test(line)) {
          // A wrapped address list ends on a separator; RFC822 folding indents instead.
          headers.set(openHeader, `${open} ${line.trim()}`.trim());
        } else {
          break;
        }
      }
    }

    return {
      bcc: headers.get('bcc') ?? '',
      body: lines.slice(index).join('\n').trim(),
      cc: headers.get('cc') ?? '',
      from: headers.get('from') ?? '',
      subject: headers.get('subject') ?? '',
      to: headers.get('to') ?? '',
    };
  },

  /** Body is left out: it would pass the ~2000 chars Outlook and Chrome truncate at. */
  toMailtoUrl({ email }: { email: ChatEmailDraft }) {
    const fields: Array<[string, string]> = [
      ['cc', ChatEmailUtil.encodeAddressList({ value: email.cc })],
      ['bcc', ChatEmailUtil.encodeAddressList({ value: email.bcc })],
      ['subject', encodeURIComponent(email.subject)],
    ];

    const search = fields
      .filter(([, value]) => value)
      .map(([name, value]) => `${name}=${value}`)
      .join('&');

    return `mailto:${ChatEmailUtil.encodeAddressList({ value: email.to })}${search ? `?${search}` : ''}`;
  },

  hasMailtoFields({ email }: { email: ChatEmailDraft }) {
    return Boolean(email.to || email.cc || email.bcc || email.subject);
  },

  /** `from` does not count - it is parsed so it is not read as body, and never shown. */
  hasVisibleFields({ email }: { email: ChatEmailDraft }) {
    return ChatEmailUtil.hasMailtoFields({ email }) || Boolean(email.body);
  },
};
